import React from 'react';
import { Mail } from 'lucide-react';
import Button from '../components/Button';
import { useTranslations } from '../context/TranslationContext';

const HeroSection: React.FC = () => {
  const { t } = useTranslations();

  return (
    <section
      id="hero"
      className="relative flex flex-col lg:flex-row items-center min-h-[65vh] md:min-h-[78vh] pt-24 md:pt-32 pb-10 md:pb-14 bg-void-blue overflow-hidden"
    >
      {/* Organic Background Shapes - floating blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute w-[600px] h-[600px] rounded-full blur-[150px] animate-blob"
          style={{
            background: 'radial-gradient(ellipse, rgba(139, 92, 246, 0.15) 0%, transparent 70%)',
            top: '-15%',
            left: '-10%',
            animation: 'blob1 25s ease-in-out infinite'
          }}
        />
        <div
          className="absolute w-[500px] h-[500px] rounded-full blur-[130px]"
          style={{
            background: 'radial-gradient(ellipse, rgba(244, 114, 182, 0.12) 0%, transparent 70%)',
            bottom: '-20%',
            right: '-5%',
            animation: 'blob2 30s ease-in-out infinite'
          }}
        />
        <div
          className="absolute w-[400px] h-[400px] rounded-full blur-[100px]"
          style={{
            background: 'radial-gradient(ellipse, rgba(45, 212, 191, 0.1) 0%, transparent 70%)',
            top: '30%',
            right: '20%',
            animation: 'blob3 20s ease-in-out infinite'
          }}
        />
      </div>

      {/* Graph animation container - mobile: above content, desktop: on the right */}
      <div className="absolute top-0 left-0 right-0 h-[600px] lg:h-full lg:right-0 lg:top-0 lg:left-[40%] opacity-50 md:opacity-80 lg:opacity-100">
        <div className="absolute inset-0 lg:rounded-l-[3rem] overflow-hidden" style={{
          left: '0',
          right: '0',
          width: '100%'
        }}>
          <OrganicGraphAnimation />
        </div>
      </div>

      <div className="relative z-10 mx-auto mt-40 w-full max-w-7xl px-6 md:mt-60 md:px-12 lg:mt-0">
        <div className="max-w-3xl">
          <div className="animate-fade-in-up">
            {/* Eyebrow */}
            <div className="inline-block mb-6 px-4 py-2 rounded-full glass-effect border border-white/20">
              <span className="text-sm font-semibold text-gradient-ai uppercase tracking-wider">{t('hero_eyebrow')}</span>
            </div>

            {/* Main Headline with Display Font */}
            <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white drop-shadow-2xl">
              {t("title1")}
              <span className="block text-gradient-ai mt-2">
                {t("title2")}
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-base md:text-lg lg:text-xl mb-8 text-gray-300 max-w-3xl font-normal leading-relaxed">
              {t("subtitle")}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Button
                variant="gradient"
                size="lg"
                icon={<Mail size={20} />}
                onClick={() => {
                  const subject = encodeURIComponent(t("demo_email_subject"));
                  const body = encodeURIComponent(t("demo_email_body"));
                  window.location.href = `mailto:info@legisratio.it?subject=${subject}&body=${body}`;
                }}
                className="w-full sm:w-auto"
              >
                {t("solutions_book_demo")}
              </Button>
            </div>
          </div>
        </div>

        {/* <div className="hidden lg:block absolute right-12 top-1/2 -translate-y-1/2 space-y-4">
          <div className="glass-effect-strong rounded-2xl p-6 border border-white/20 hover-lift animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="text-4xl font-bold text-gradient-ai mb-2">1.4x</div>
            <div className="text-sm text-gray-300">Daily Productivity</div>
            <div className="text-xs text-gray-500 mt-1"></div>
          </div>
          <div className="glass-effect-strong rounded-2xl p-6 border border-white/20 hover-lift animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <div className="text-4xl font-bold text-gradient-ai mb-2">0</div>
            <div className="text-sm text-gray-300">Errors</div>
            <div className="text-xs text-gray-500 mt-1">100% traceable</div>
          </div>
          <div className="glass-effect-strong rounded-2xl p-6 border border-white/20 hover-lift animate-fade-in" style={{ animationDelay: '0.6s' }}>
            <div className="text-4xl font-bold text-gradient-ai mb-2">75%</div>
            <div className="text-sm text-gray-300">Time savings</div>
            <div className="text-xs text-gray-500 mt-1">Average reduction</div>
          </div>
        </div> */}
      </div>

      {/* CSS for organic blob animations */}
      <style>{`
        @keyframes blob1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(30px, -50px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.95); }
          75% { transform: translate(50px, 30px) scale(1.05); }
        }
        @keyframes blob2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-40px, 30px) scale(1.08); }
          66% { transform: translate(30px, -40px) scale(0.92); }
        }
        @keyframes blob3 {
          0%, 100% { transform: translate(0, 0) scale(1) rotate(0deg); }
          50% { transform: translate(-30px, 40px) scale(1.1) rotate(10deg); }
        }
      `}</style>
    </section>
  );
};

// Organic Graph Animation Component with natural movement
const OrganicGraphAnimation: React.FC = () => {
  const svgRef = React.useRef<SVGSVGElement>(null);

  React.useEffect(() => {
    if (!svgRef.current) return;

    const svg = svgRef.current;
    const width = svg.clientWidth;
    const height = svg.clientHeight;
    const padding = 50;

    // Detect mobile
    const isMobile = window.innerWidth < 768;

    // Adjust parameters based on screen size
    const nodeCount = isMobile ? 18 : 35;
    const connectionDistance = isMobile ? 180 : 250;

    // Simplex-like noise function for organic movement
    const noise = (x: number, y: number, t: number) => {
      const n1 = Math.sin(x * 0.01 + t * 0.3) * Math.cos(y * 0.01 + t * 0.2);
      const n2 = Math.sin(x * 0.02 - t * 0.15) * Math.sin(y * 0.015 + t * 0.1);
      const n3 = Math.cos(x * 0.008 + y * 0.008 + t * 0.25);
      return (n1 + n2 + n3) / 3;
    };

    // Color palette for natural gradient
    const colors = {
      primary: { r: 139, g: 92, b: 246 },   // Mystic Violet
      secondary: { r: 45, g: 212, b: 191 }, // Cosmic Teal
      tertiary: { r: 244, g: 114, b: 182 }, // Nebula Pink
    };

    // Interpolate between colors based on value
    const lerpColor = (t: number): string => {
      const clampedT = Math.max(0, Math.min(1, t));
      let r: number, g: number, b: number;

      if (clampedT < 0.5) {
        const localT = clampedT * 2;
        r = colors.primary.r + (colors.secondary.r - colors.primary.r) * localT;
        g = colors.primary.g + (colors.secondary.g - colors.primary.g) * localT;
        b = colors.primary.b + (colors.secondary.b - colors.primary.b) * localT;
      } else {
        const localT = (clampedT - 0.5) * 2;
        r = colors.secondary.r + (colors.tertiary.r - colors.secondary.r) * localT;
        g = colors.secondary.g + (colors.tertiary.g - colors.secondary.g) * localT;
        b = colors.secondary.b + (colors.tertiary.b - colors.secondary.b) * localT;
      }

      return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
    };

    interface Node {
      baseX: number;
      baseY: number;
      x: number;
      y: number;
      radius: number;
      phase: number;
      amplitude: number;
      breatheSpeed: number;
      colorOffset: number;
    }

    // Create nodes with organic properties
    const nodes: Node[] = [];

    for (let i = 0; i < nodeCount; i++) {
      const baseX = padding + Math.random() * (width - 2 * padding);
      const baseY = padding + Math.random() * (height - 2 * padding);

      nodes.push({
        baseX,
        baseY,
        x: baseX,
        y: baseY,
        radius: 3 + Math.random() * 5,
        phase: Math.random() * Math.PI * 2,
        amplitude: 20 + Math.random() * 40,
        breatheSpeed: 0.5 + Math.random() * 1.5,
        colorOffset: Math.random(),
      });
    }

    let animationId: number;
    let time = 0;

    const animate = () => {
      time += 0.016; // ~60fps timestep

      // Clear previous frame
      while (svg.firstChild) {
        svg.removeChild(svg.firstChild);
      }

      // Create defs for filters and gradients
      const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');

      // Add glow filter for nodes
      const glowFilter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
      glowFilter.setAttribute('id', 'glow');
      glowFilter.setAttribute('x', '-50%');
      glowFilter.setAttribute('y', '-50%');
      glowFilter.setAttribute('width', '200%');
      glowFilter.setAttribute('height', '200%');

      const feGaussianBlur = document.createElementNS('http://www.w3.org/2000/svg', 'feGaussianBlur');
      feGaussianBlur.setAttribute('in', 'SourceGraphic');
      feGaussianBlur.setAttribute('stdDeviation', '3');
      feGaussianBlur.setAttribute('result', 'blur');
      glowFilter.appendChild(feGaussianBlur);

      const feMerge = document.createElementNS('http://www.w3.org/2000/svg', 'feMerge');
      const feMergeNode1 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
      feMergeNode1.setAttribute('in', 'blur');
      const feMergeNode2 = document.createElementNS('http://www.w3.org/2000/svg', 'feMergeNode');
      feMergeNode2.setAttribute('in', 'SourceGraphic');
      feMerge.appendChild(feMergeNode1);
      feMerge.appendChild(feMergeNode2);
      glowFilter.appendChild(feMerge);

      defs.appendChild(glowFilter);
      svg.appendChild(defs);

      // Update node positions with organic movement
      nodes.forEach((node) => {
        // Use noise for natural wandering
        const noiseX = noise(node.baseX, node.baseY, time) * node.amplitude;
        const noiseY = noise(node.baseY, node.baseX, time + 100) * node.amplitude;

        // Add gentle circular motion
        const circularX = Math.sin(time * 0.3 + node.phase) * 15;
        const circularY = Math.cos(time * 0.25 + node.phase) * 15;

        node.x = node.baseX + noiseX + circularX;
        node.y = node.baseY + noiseY + circularY;

        // Keep within bounds with soft edges
        node.x = Math.max(padding, Math.min(width - padding, node.x));
        node.y = Math.max(padding, Math.min(height - padding, node.y));
      });

      // Draw flowing connections with curved paths
      nodes.forEach((node, i) => {
        nodes.slice(i + 1).forEach((other) => {
          const dx = other.x - node.x;
          const dy = other.y - node.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < connectionDistance) {
            const opacity = 0.4 * Math.pow(1 - dist / connectionDistance, 1.5);

            // Calculate control point for bezier curve (organic curvature)
            const midX = (node.x + other.x) / 2;
            const midY = (node.y + other.y) / 2;

            // Add perpendicular offset based on noise for organic curves
            const perpX = -dy / dist;
            const perpY = dx / dist;
            const curveAmount = noise(midX, midY, time * 0.5) * 30;

            const ctrlX = midX + perpX * curveAmount;
            const ctrlY = midY + perpY * curveAmount;

            // Create curved path
            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', `M ${node.x} ${node.y} Q ${ctrlX} ${ctrlY} ${other.x} ${other.y}`);
            path.setAttribute('fill', 'none');
            path.setAttribute('stroke', lerpColor((node.colorOffset + other.colorOffset) / 2 + time * 0.05 % 1));
            path.setAttribute('stroke-opacity', opacity.toString());
            path.setAttribute('stroke-width', '1.5');
            path.setAttribute('stroke-linecap', 'round');
            svg.appendChild(path);
          }
        });
      });

      // Draw nodes with breathing effect and natural colors
      nodes.forEach((node) => {
        // Breathing effect on radius
        const breathe = 1 + Math.sin(time * node.breatheSpeed + node.phase) * 0.2;
        const currentRadius = node.radius * breathe;

        // Color based on position and time for organic feel
        const colorValue = (node.colorOffset + time * 0.03) % 1;
        const nodeColor = lerpColor(colorValue);

        // Outer glow
        const glowCircle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        glowCircle.setAttribute('cx', node.x.toString());
        glowCircle.setAttribute('cy', node.y.toString());
        glowCircle.setAttribute('r', (currentRadius * 2).toString());
        glowCircle.setAttribute('fill', nodeColor);
        glowCircle.setAttribute('fill-opacity', (0.15 * breathe).toString());
        svg.appendChild(glowCircle);

        // Inner node
        const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        circle.setAttribute('cx', node.x.toString());
        circle.setAttribute('cy', node.y.toString());
        circle.setAttribute('r', currentRadius.toString());
        circle.setAttribute('fill', nodeColor);
        circle.setAttribute('fill-opacity', (0.85 * breathe).toString());
        circle.setAttribute('filter', 'url(#glow)');
        svg.appendChild(circle);
      });

      // Add floating particles for extra organic feel
      const particleCount = isMobile ? 8 : 15;
      for (let i = 0; i < particleCount; i++) {
        const particlePhase = (i / particleCount) * Math.PI * 2;
        const px = width * 0.3 + Math.sin(time * 0.2 + particlePhase) * width * 0.4;
        const py = height * 0.5 + Math.cos(time * 0.15 + particlePhase * 1.5) * height * 0.3;
        const particleOpacity = 0.15 + Math.sin(time * 0.5 + particlePhase) * 0.1;

        const particle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
        particle.setAttribute('cx', px.toString());
        particle.setAttribute('cy', py.toString());
        particle.setAttribute('r', '2');
        particle.setAttribute('fill', '#2DD4BF');
        particle.setAttribute('fill-opacity', particleOpacity.toString());
        svg.appendChild(particle);
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <svg ref={svgRef} className="w-full h-full" style={{
      background: 'transparent'
    }} />
  );
};

export default HeroSection;
